import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button, InputText, InputPassword, Checkbox, FloatingField } from '../../components/aip';

// スタイルをインポート
import '../../styles/aip/index.css';

interface LocationState {
  from?: {
    pathname: string;
  };
}

interface FieldErrors {
  email?: string;
  password?: string;
}

/**
 * ログインページコンポーネント
 */
export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ログイン後のリダイレクト先を取得
  const from = (location.state as LocationState)?.from?.pathname || '/meeting/manage-new';

  /**
   * フォームのバリデーション
   * @returns バリデーションエラーがある場合はtrue
   */
  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    // メールアドレスのバリデーション
    if (!email.trim()) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = '有効なメールアドレスを入力してください';
    }

    // パスワードのバリデーション
    if (!password) {
      errors.password = 'パスワードを入力してください';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length > 0;
  };

  /**
   * フィールドのエラーをクリア
   */
  const clearFieldError = (field: keyof FieldErrors) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // バリデーション実行
    if (validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await login(email, password);
      // ログイン成功後、元のページまたはダッシュボードにリダイレクト
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ログインに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="aip-html-body">
      <div className="aip-main" data-size="xs">
        <header className="aip-main__header">
          <div className="aip-main__header-container">
            <h1 className="aip-main__header-title">AiiPo Management</h1>
          </div>
        </header>

        <div className="aip-main__content">
          <h2 style={{ fontSize: '1.5rem', fontWeight: 500, marginBottom: '1rem' }}>ログイン</h2>
          
          {error && (
            <div
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: 'var(--color-red50)',
                color: 'var(--color-alert)',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                fontSize: '0.875rem',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="aip-form-container__body">
              {/* メールアドレス */}
              <FloatingField
                label="メールアドレス"
                htmlFor="email"
                column="2"
                required
                errorText={fieldErrors.email}
                hasError={!!fieldErrors.email}
              >
                <InputText
                  id="email"
                  name="email"
                  type="email"
                  placeholder="example@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearFieldError('email');
                  }}
                  autoComplete="email"
                  className="aip-floating-field__control"
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                />
              </FloatingField>

              {/* パスワード */}
              <FloatingField
                label="パスワード"
                htmlFor="password"
                column="2"
                required
                errorText={fieldErrors.password}
                hasError={!!fieldErrors.password}
              >
                <InputPassword
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearFieldError('password');
                  }}
                  autoComplete="current-password"
                  aria-invalid={!!fieldErrors.password}
                  aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                />
              </FloatingField>

              {/* ログイン状態を保持 */}
              <div data-column="2">
                <Checkbox
                  size="sm"
                  name="remember"
                  id="remember"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  label="ログイン状態を保持する"
                />
              </div>

              {/* ログインボタン */}
              <div data-column="2" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <Button
                  buttonType="solid-fill"
                  size="md"
                  type="submit"
                  disabled={isLoading}
                  style={{ width: '100%' }}
                >
                  {isLoading ? 'ログイン中...' : 'ログイン'}
                </Button>

                <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <Link to="/meeting/manage-new/password-reset" className="aip-link">
                    パスワードをお忘れですか？
                  </Link>
                  <Link to="/meeting/manage-new/register" className="aip-link">
                    新規登録はこちら
                  </Link>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;

